import { stripInline } from "@/components/docs/inline";
import { docHref, docSectionFor } from "@/content/docs/index";
import type { DocPage } from "@/content/docs/types";
import { BRAND_NAME } from "@/lib/brand";

const ORIGIN = "https://mavori.ai";
const ORGANIZATION_ID = `${ORIGIN}/#organization`;
const WEBSITE_ID = `${ORIGIN}/#website`;

/**
 * Structured data for documentation pages.
 *
 * `TechArticle` carries the freshness fields answer engines read to judge
 * whether a page is current; `FAQPage` and `HowTo` label question/answer and
 * procedure content so an extractor does not have to infer either from
 * prose; `BreadcrumbList` states where the page sits.
 *
 * Everything is derived from the authored blocks, so structured data cannot
 * drift away from what the page actually says.
 */
const PUBLISHER = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: BRAND_NAME,
  url: `${ORIGIN}/`,
  logo: `${ORIGIN}/logo-transparent.png`,
};

export function docPageJsonLd(page: DocPage): Record<string, unknown>[] {
  const url = `${ORIGIN}${docHref(page.slug)}`;
  const section = docSectionFor(page.slug);
  const graph: Record<string, unknown>[] = [];

  graph.push({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "@id": `${url}#article`,
    headline: page.title,
    name: page.title,
    description: page.description,
    url,
    inLanguage: "en",
    dateModified: page.updated,
    datePublished: page.published,
    author: PUBLISHER,
    publisher: PUBLISHER,
    isPartOf: {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
    },
    articleSection: section?.label,
  });

  graph.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: BRAND_NAME, item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Documentation", item: `${ORIGIN}/docs` },
      ...(section && page.slug !== "index"
        ? [{ "@type": "ListItem", position: 3, name: section.label }]
        : []),
      ...(page.slug !== "index"
        ? [
            {
              "@type": "ListItem",
              position: section ? 4 : 3,
              name: page.title,
              item: url,
            },
          ]
        : []),
    ],
  });

  const faq = page.blocks.find((b) => b.type === "faq");
  if (faq && faq.type === "faq" && faq.items.length > 0) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: faq.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: stripInline(item.a) },
      })),
    });
  }

  // Only an unambiguous single procedure becomes a HowTo — two steps blocks
  // on one page would make the labelling a guess.
  const stepBlocks = page.blocks.filter((b) => b.type === "steps");
  const steps = stepBlocks.length === 1 ? stepBlocks[0] : undefined;
  if (steps && steps.type === "steps" && steps.items.length > 1) {
    graph.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "@id": `${url}#howto`,
      name: page.title,
      description: page.description,
      step: steps.items.map((step, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: step.title,
        text: stripInline(step.text),
        url: `${url}#step-${i + 1}`,
      })),
    });
  }

  return graph;
}

// `CollectionPage`, not `WebSite`: index.html already declares the site-level
// Organization + WebSite graph on every page, and a second WebSite node would
// leave a parser choosing between two entities for the same site.
export function docsHubJsonLd(pageCount: number): Record<string, unknown>[] {
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${ORIGIN}/docs#collection`,
      name: `${BRAND_NAME} documentation`,
      url: `${ORIGIN}/docs`,
      publisher: { "@id": ORGANIZATION_ID },
      description: `${pageCount} concise guides to ${BRAND_NAME}'s Agent, research, strategies, portfolio, supervised trading, automation, and connections.`,
    },
  ];
}
